class SmoothScroll {
  constructor() {
    this.init();
  }

  init() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (event) => {
        event.preventDefault();
        const target = document.querySelector(anchor.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }
}

class ScrollRestoration {
  constructor() {
    this.storagePrefix = 'farmacia-scroll:';
    this.maxAgeMs = 24 * 60 * 60 * 1000;

    if (!window.sessionStorage) return;

    this.init();
  }

  init() {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    this.saveThrottled = this.throttle(() => this.saveCurrentPosition(), 120);

    window.addEventListener('scroll', this.saveThrottled, { passive: true });
    window.addEventListener('pagehide', () => this.saveCurrentPosition());
    window.addEventListener('beforeunload', () => this.saveCurrentPosition());
    window.addEventListener('pageshow', (event) => this.restoreIfNeeded(event));

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.saveCurrentPosition();
      }
    });

    this.cleanupStaleEntries();
    this.restoreIfNeeded();
  }

  getCurrentKey() {
    return `${this.storagePrefix}${window.location.pathname}${window.location.search}`;
  }

  saveCurrentPosition() {
    try {
      const state = {
        x: window.scrollX || window.pageXOffset || 0,
        y: window.scrollY || window.pageYOffset || 0,
        ts: Date.now()
      };

      sessionStorage.setItem(this.getCurrentKey(), JSON.stringify(state));
    } catch (error) {
    }
  }

  shouldRestore(event) {
    if (event && event.persisted) return true;

    const navigationEntry = performance.getEntriesByType('navigation')[0];
    if (navigationEntry && navigationEntry.type === 'back_forward') return true;

    if (performance.navigation && performance.navigation.type === 2) return true;

    return false;
  }

  restoreIfNeeded(event = null) {
    if (!this.shouldRestore(event)) return;

    let savedStateRaw = null;
    try {
      savedStateRaw = sessionStorage.getItem(this.getCurrentKey());
    } catch (error) {
      return;
    }

    if (!savedStateRaw) return;

    let savedState;
    try {
      savedState = JSON.parse(savedStateRaw);
    } catch (error) {
      return;
    }

    if (!savedState || typeof savedState.y !== 'number') return;

    requestAnimationFrame(() => {
      window.scrollTo({ top: savedState.y, left: savedState.x || 0, behavior: 'auto' });
      requestAnimationFrame(() => {
        window.scrollTo(savedState.x || 0, savedState.y);
      });
    });
  }

  cleanupStaleEntries() {
    const now = Date.now();

    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(this.storagePrefix)) continue;

      try {
        const stored = JSON.parse(sessionStorage.getItem(key));
        if (!stored || typeof stored.ts !== 'number' || now - stored.ts > this.maxAgeMs) {
          sessionStorage.removeItem(key);
          i -= 1;
        }
      } catch (error) {
        sessionStorage.removeItem(key);
        i -= 1;
      }
    }
  }

  throttle(fn, wait) {
    let lastTime = 0;

    return (...args) => {
      const now = Date.now();
      if (now - lastTime < wait) return;
      lastTime = now;
      fn(...args);
    };
  }
}

function initializeMobileRedirects() {
  if (document.documentElement.dataset.mobileRedirectsInitialized === 'true') {
    return;
  }

  document.documentElement.dataset.mobileRedirectsInitialized = 'true';

  const mobileButtonSelector = [
    '.farmacia-hero-mobile-switch__cta',
    '.cta-box__button',
    '.btn-view-all-glass',
    '.btn-consultar',
    '.farmacia-btn-buy',
    '.collection-editorial-cta__button'
  ].join(', ');

  let lastHandledLink = null;
  let lastHandledAt = 0;

  function isMobileViewport() {
    return window.matchMedia('(max-width: 1199px)').matches;
  }

  function navigateToLink(link) {
    const href = link.getAttribute('href');

    if (!href || href === '#' || href.startsWith('javascript:')) {
      return;
    }

    if (link.target === '_blank') {
      window.open(href, '_blank', 'noopener');
      return;
    }

    window.location.href = href;
  }

  function handleRedirect(event) {
    if (!isMobileViewport()) {
      return;
    }

    const link = event.target.closest(mobileButtonSelector);

    if (!link || link.tagName !== 'A') {
      return;
    }

    const now = Date.now();
    const isDuplicateTrigger = lastHandledLink === link && now - lastHandledAt < 700;

    if (isDuplicateTrigger) {
      event.preventDefault();
      return;
    }

    lastHandledLink = link;
    lastHandledAt = now;
    event.preventDefault();
    navigateToLink(link);
  }

  document.addEventListener('touchend', handleRedirect, { passive: false });
  document.addEventListener('click', handleRedirect);
}

function initializeFarmaciaCoreUtils() {
  new ScrollRestoration();
  new SmoothScroll();
  initializeMobileRedirects();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFarmaciaCoreUtils, { once: true });
} else {
  initializeFarmaciaCoreUtils();
}

if (typeof window !== 'undefined') {
  window.FarmaciaUtils = Object.assign(window.FarmaciaUtils || {}, {
    ScrollRestoration,
    SmoothScroll,
    initializeMobileRedirects
  });
}
